import { useDisclosure } from "@mantine/hooks";
import { useField } from "formik";
import { type CSSProperties, type Ref, forwardRef, useId } from "react";
import { t } from "ttag";

import {
  Button,
  Input,
  Stack,
  Text,
  TextInput,
  type TextInputProps,
} from "metabase/ui";

import S from "./FormSecretKey.module.css";
import { SetupKeyModal } from "./SetupKeyModal";

export interface FormSecretKeyProps
  extends Omit<TextInputProps, "value" | "error"> {
  name: string;
  nullable?: boolean;
}

type GenerateButtonStyle = CSSProperties & {
  "--form-secret-key-button-background": string;
  "--form-secret-key-button-background-hover": string;
  "--form-secret-key-button-color": string;
};

const GENERATE_BUTTON_STYLE: GenerateButtonStyle = {
  "--form-secret-key-button-background":
    "var(--mb-color-background_surface-brand-strong)",
  "--form-secret-key-button-background-hover":
    "var(--mb-color-background_surface-brand-strong-hover)",
  "--form-secret-key-button-color": "var(--mb-color-text-primary-inverse)",
};

export const FormSecretKey = forwardRef(function FormSecretKey(
  {
    name,
    nullable: _nullable,
    onChange: _onChange,
    onBlur: _onBlur,
    readOnly,
    label,
    description,
    required,
    id,
    wrapperProps,
    ...props
  }: FormSecretKeyProps,
  ref: Ref<HTMLInputElement>,
) {
  const [showModal, { open: openModal, close: closeModal }] = useDisclosure();
  const [{ value }, { error }, { setValue }] = useField(name);
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const actionLabelId = `${controlId}-action`;
  const hasSecret = Boolean(value);

  return (
    <>
      <Stack gap="sm">
        {hasSecret || readOnly ? (
          <TextInput
            {...props}
            ref={ref}
            id={controlId}
            name={name}
            label={label}
            description={description}
            required={required}
            readOnly
            value={obfuscateValue(value)}
            wrapperProps={wrapperProps}
            classNames={{
              wrapper: S.inputWrapper,
              input: S.input,
            }}
            rightSection={
              !readOnly ? (
                <Button
                  className={S.generateButton}
                  style={GENERATE_BUTTON_STYLE}
                  onClick={openModal}
                  variant="filled"
                >
                  {t`Regenerate key`}
                </Button>
              ) : undefined
            }
            rightSectionProps={
              !readOnly ? { className: S.rightSection } : undefined
            }
          />
        ) : (
          <Input.Wrapper
            id={controlId}
            label={label}
            description={description}
            required={required}
            {...wrapperProps}
          >
            <Button
              id={controlId}
              className={S.generateButton}
              style={GENERATE_BUTTON_STYLE}
              miw="10rem"
              onClick={openModal}
              variant="filled"
              aria-labelledby={`${controlId}-label ${actionLabelId}`}
            >
              <span id={actionLabelId}>{t`Set up key`}</span>
            </Button>
          </Input.Wrapper>
        )}
        {!!error && <Text c="feedback-negative">{error}</Text>}
      </Stack>
      {showModal && (
        <SetupKeyModal
          onConfirm={(newValue) => {
            setValue(newValue);
            closeModal();
          }}
          onClose={closeModal}
          currentValue={value}
        />
      )}
    </>
  );
});

const obfuscateValue = (value: string) =>
  value ? "**********" + value.slice(-2) : "";
